import { ButtonPrimary } from "components/Atoms/Button";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader } from "components/PreviewPanel/PreviewPanel";
import React, { useEffect } from "react";
import { Message } from "semantic-ui-react";
import Article from "./article";
import { PlaceholderContent } from "./articleplaceholders";
import { getArticleUrl, type RelevantArticle } from "./model";

interface SupportPreviewPanelState {
  title: string
  content: string
  slug: string
  relevantArticles: RelevantArticle[]
  hasError: boolean
}

interface SupportPreviewPanelProps {
  articleId: number
  trigger: React.ReactElement
}

interface SupportPreviewPanelContentProps {
  articleId: number
}

export const SupportPreviewPanel = (props: SupportPreviewPanelProps) => {
  const { articleId, trigger } = props;

  return <div>
    <PreviewPanel
      id={`support-article-${articleId}`}
      size="medium"
      trigger={trigger}
    >
      <SupportPreviewPanelContent articleId={articleId} />
    </PreviewPanel>
  </div>;
}

const SupportPreviewPanelContent = (props: SupportPreviewPanelContentProps) => {
  const { articleId } = props;
  const [state, setState] = React.useState<SupportPreviewPanelState>({
    title: "",
    content: null,
    slug: "",
    relevantArticles: [],
    hasError: false
  });

  useEffect(() => {
    const fetchSupportArticle = async () => {
      try {
        const result = await window.CoreApi.Article.GetArticle(articleId);

        const translations = result.data.data.translations;

        const uiLanguage = serverReference.languages.find(lang => lang.twoLetterTag === site.culture.uiLanguage)?.value ?? "english";

        const translation = translations.find(trans => trans.language === uiLanguage) ||
          translations.find(trans => trans.language === "english");

        const relevantArticles: RelevantArticle[] = result.data.data.relatedArticles.map<RelevantArticle>(rel => {
          const relevantArticleTranslation = rel.translations.find(t => t.language === uiLanguage) ?? rel.translations.find(t => t.language === "english");
          return {
            title: relevantArticleTranslation.title,
            slug: relevantArticleTranslation.slug,
            articleId: rel.articleId
          }
        });

        setState({
          relevantArticles,
          content: translation.content,
          title: translation.title,
          slug: translation.slug,
          hasError: false
        });
      } catch {
        setState({ ...state, hasError: true });
      }
    };

    fetchSupportArticle();
  }, []);

  if (state.hasError) {
    return <>
      <PreviewPanelHeader title={RESX.GeneralWarnings.Warning} />
      <PreviewPanelContent>
        <Message color="red">{RESX.GeneralLabels.SomethingUnexpectedHappened}</Message>
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  if (state.content === null) {
    return <>
      <PreviewPanelHeader title="" />
      <PlaceholderContent />
      <PreviewPanelFooter />
    </>;
  }

  const contentEl = () => <div dangerouslySetInnerHTML={{ __html: state.content }}></div>;
  const articleUrl = getArticleUrl(articleId, state.slug);

  return <>
    <PreviewPanelHeader title={RESX.Feed.Support} />
    <PreviewPanelContent>
      <div className="nav-text-semibold">{state.title}</div>
      <Article
        inPreviewPanel
        renderContent={contentEl}
        relevantArticles={state.relevantArticles}
      />
    </PreviewPanelContent>
    <PreviewPanelFooter>
      <ButtonPrimary label={RESX.GeneralLabels.ReadMore} href={articleUrl} target="_blank" />
    </PreviewPanelFooter>
  </>;
};
